<template>
  <div class="tpl-strategy-editor">
    <div class="top-container">
      <el-radio-group v-model="radio">
        <el-radio border label="1">工作日策略</el-radio>
        <el-radio border label="2">休息日策略</el-radio>
      </el-radio-group>

      <div class="operate-btns-container">
        <el-button plain class="edit" @click="startEdit" v-if="!isEdit">编辑</el-button>
        <el-button plain class="copy" @click="copyStrategy">复制</el-button>
        <el-button plain class="paste" @click="pasteStrategy" :disabled="!isEdit">粘贴</el-button>
        <el-button plain class="clear" @click="tableData = []" :disabled="!isEdit">清空</el-button>
      </div>
    </div>

    <div class="main-container">
      <!-- 表格 -->
      <el-table :data="tableData" height="200" ref="tableRef" scrollbar-always-on
        row-class-name="table-row" cell-class-name="table-cell" header-cell-class-name="table-header-cell">
        <el-table-column label="开始时间">
          <template #default="{ row }">
            <div class="cell-content">
              <input type="time" class="s-underline-input" v-model="row.start_time" :disabled="!isEdit">
            </div>
          </template>
        </el-table-column>
        <el-table-column label="结束时间">
          <template #default="{ row }">
            <div class="cell-content">
              <input type="time" class="s-underline-input" v-model="row.end_time" :disabled="!isEdit">
            </div>
          </template>
        </el-table-column>
        <el-table-column label="功率(kW)">
          <template #default="{ row }">
            <div class="cell-content">
              <input type="number" class="s-underline-input" v-model="row.power" :disabled="!isEdit"
                @change="inputPowerChange">
            </div>
          </template>
        </el-table-column>
        <el-table-column label="上限(%)">
          <template #default="{ row }">
            <div class="cell-content">
              <input type="number" class="s-underline-input" v-model="row.upper_limit" :disabled="!isEdit" :min="0"
                :max="100" @change="onUpperLimitChange(row)">
            </div>
          </template>
        </el-table-column>
        <el-table-column label="下限(%)">
          <template #default="{ row }">
            <div class="cell-content">
              <input type="number" class="s-underline-input" v-model="row.lower_limit" :disabled="!isEdit" :min="0"
                :max="100" @change="onLowerLimitChange(row)">
            </div>
          </template>
        </el-table-column>
        <el-table-column label="模式" width="180">
          <template #default="{ row }">
            <div class="cell-content mode-cell">
              <el-select v-model="row.running_mode" :disabled="!isEdit" placeholder="请选择" class="mode-select"
                size="default">
                <el-option v-for="opt in STRATEGY_MODE_OPTIONS" :key="opt.value" :label="opt.label"
                  :value="opt.value" />
              </el-select>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="260" fixed="right">
          <template #default="{ row }">
            <div class="operate-cell-container">
              <el-button plain class="add" @click="addLine(row)" :disabled="!isEdit">插入</el-button>
              <el-button plain class="delete" @click="removeLine(row)" :disabled="!isEdit">删除</el-button>
              <el-button plain class="move" :disabled="!isEdit" @mousedown="onSortBtnDown(row)"
                :class="isEdit ? 'cursor-move' : ''">切换</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <el-button @click="addToEnd" :icon="Plus" class="add-btn" plain :disabled="!isEdit">新增一行</el-button>

      <!-- 移动时的定位条 -->
      <div class="move-bar" :style="{ top: barTop + 'px' }" v-show="visible"></div>

      <!-- 甘特图 -->
      <div class="chart-container">
        <v-chart :option="option" :autoresize="true"></v-chart>
      </div>
    </div>

    <!-- 粘贴策略 -->
    <el-dialog title="策略文本" v-model="pasteDialogVisible" append-to-body>
      <div class="tip-text">请将策略文本粘贴到这里</div>
      <TextEditor v-model="pastedValue" />
      <template #footer>
        <el-button @click="pasteDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="onPastedConfirm">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, ref, watch, getCurrentInstance, onMounted, onUnmounted, nextTick } from 'vue'
import { Plus } from '@element-plus/icons-vue'
import VChart from 'vue-echarts'
import TextEditor from '@/views/station-monitor/detail/tabs/StrategyController/components/TextEditor/index.vue'
import {
  createOption,
  strategyData2CamelCase,
  checkStrategyValidate,
  checkStrategyListValidate,
  isTimeSlotValid,
  transformJSON,
  timeToMinutes,
  minutesOfDay,
  getChartOption,
  cloneDeep,
  STRATEGY_MODE_OPTIONS,
} from '../utils.js'

const props = defineProps({
  isEdit: { type: Boolean, default: false },
  // 模板策略：{ strategys_work: [], strategys_rest: [] }
  strategyData: { type: Object, default: () => ({}) },
  // 模板无具体设备，功率不做上限校验，给一个宽松上限
  powerLimit: { type: Number, default: 100000 },
})
const emit = defineEmits(['update:isEdit'])

const { proxy } = getCurrentInstance()

const isEdit = computed({
  get() { return props.isEdit },
  set(v) { emit('update:isEdit', v) },
})

const radio = ref('1')
const option = ref(getChartOption())

const workStrategy = ref([])
const restStrategy = ref([])

const tableRef = ref(null)
const pasteDialogVisible = ref(false)
const pastedValue = ref('')

const tableData = computed({
  get() {
    return radio.value === '1' ? workStrategy.value : restStrategy.value
  },
  set(value) {
    if (radio.value === '1') workStrategy.value = value
    else restStrategy.value = value
  },
})

/** 用外部策略数据初始化编辑器 */
function updateLocalStrategy() {
  const value = props.strategyData || {}
  workStrategy.value = normalizeStrategies(cloneDeep(value.strategys_work || []))
  restStrategy.value = normalizeStrategies(cloneDeep(value.strategys_rest || []))
}

/** 归一化策略数据：确保 running_mode 为数字类型 */
function normalizeStrategies(strategies) {
  return strategies.map(s => ({
    ...s,
    running_mode: Number(s.running_mode ?? 0)
  }))
}

/** 获取编辑完成的策略 */
function getStrategys(isEffective = true, transform2Camel) {
  return {
    work: isEffective ? getEffectiveStrategy(workStrategy.value, transform2Camel) : workStrategy.value,
    rest: isEffective ? getEffectiveStrategy(restStrategy.value, transform2Camel) : restStrategy.value,
  }
}

function getEffectiveStrategy(value = [], transform2Camel = false) {
  const result = []
  for (const strategy of value) {
    const camelStrategy = strategyData2CamelCase(strategy)
    if (checkStrategyValidate(camelStrategy, props.powerLimit)) {
      result.push(transform2Camel ? camelStrategy : strategy)
    }
  }
  return result
}

/** 策略变化更新甘特图 */
watch(tableData, () => {
  const strategyList = getEffectiveStrategy(tableData.value)
  option.value = createOption(strategyList, props.powerLimit)
}, { deep: true, immediate: true })

function createOneStrategy(startTime = '') {
  return {
    start_time: startTime,
    end_time: '',
    power: '0',
    upper_limit: '100.0',
    lower_limit: '0.0',
    running_mode: 0,
    threshold_val: 10,
  }
}

/** 上一行结束时间作为新行开始时间（结束时间已到 23:59 则不接续） */
function nextStartFrom(row) {
  const end = row?.end_time
  if (end && end !== '23:59' && end !== '24:00') return end
  return ''
}

function addLine(row) {
  const index = tableData.value.indexOf(row)
  tableData.value.splice(index + 1, 0, createOneStrategy(nextStartFrom(row)))
}

function addToEnd() {
  const last = tableData.value[tableData.value.length - 1]
  tableData.value.push(createOneStrategy(nextStartFrom(last)))
}

function removeLine(row) {
  tableData.value = tableData.value.filter(item => item !== row)
}

function startEdit() {
  isEdit.value = true
}

function exitEdit() {
  isEdit.value = false
  updateLocalStrategy()
}

function copyStrategy() {
  const text = JSON.stringify(transformJSON(tableData.value, false), null, 2)
  copyText(text)
  window.dispatchNotify({ type: 'success', message: '复制成功!' })
}

function pasteStrategy() {
  pasteDialogVisible.value = true
  pastedValue.value = ''
}

function onPastedConfirm() {
  const s = pastedValue.value
  const isStrArr = (str) => str.startsWith('[') && str.endsWith(']')
  if (!s) {
    pasteDialogVisible.value = false
    return
  }
  if (!isStrArr(s)) {
    nextTick(() => window.dispatchNotify({ type: 'info', message: '策略格式或数据不正确!' }))
    return
  }
  try {
    const strategy = JSON.parse(s)
    if (!checkStrategyListValidate(strategy, props.powerLimit)) {
      nextTick(() => window.dispatchNotify({ type: 'info', message: '策略格式不正确!' }))
      return
    }
    if (tableData.value.length > 0) {
      proxy.$confirm('已经存在策略，确定要替换吗？', '提示', {
        confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning',
      }).then(() => { tableData.value = strategy }).catch((e) => console.warn(e))
    } else {
      tableData.value = strategy
    }
    pasteDialogVisible.value = false
  } catch (error) {
    console.warn(error)
    window.dispatchNotify({ type: 'info', message: '粘贴失败!' })
  }
}

function copyText(text) {
  const textarea = document.createElement('textarea')
  textarea.setAttribute('readonly', 'readonly')
  textarea.value = text
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

function coverTest(strategyList) {
  let sumMinutes = 0
  for (const strategy of strategyList) {
    sumMinutes += timeToMinutes(strategy.end_time) - timeToMinutes(strategy.start_time)
  }
  return minutesOfDay - 1 === sumMinutes || minutesOfDay === sumMinutes
}

/** 校验策略有效性 */
function checkValidate() {
  if (tableData.value.some(it => it.power === '')) {
    window.dispatchNotify({ type: 'info', message: '功率值不能为空，请检查！' })
    return false
  }
  if (tableData.value.some(it => it.upper_limit === '')) {
    window.dispatchNotify({ type: 'info', message: '充电上限不能为空，请检查！' })
    return false
  }
  if (tableData.value.some(it => it.lower_limit === '')) {
    window.dispatchNotify({ type: 'info', message: '放电下限不能为空，请检查！' })
    return false
  }
  if (tableData.value.some(it => !isInteger(it.power))) {
    window.dispatchNotify({ type: 'info', message: '功率值不能为小数，请检查！' })
    return false
  }

  const strategys = getStrategys(false)
  if (!checkStrategyListValidate(strategys.rest, props.powerLimit) ||
    !checkStrategyListValidate(strategys.work, props.powerLimit)) {
    return false
  }

  if (strategys.work.length < 1) {
    window.dispatchNotify({ type: 'info', message: '请设置工作日策略!' })
    return false
  }
  if (strategys.rest.length < 1) {
    window.dispatchNotify({ type: 'info', message: '请设置休息日策略!' })
    return false
  }

  const text = '策略时间槽不正确，请设置连续的时段，且范围覆盖 00:00-23:59'
  if (!isTimeSlotValid(strategys.work) || !coverTest(strategys.work)) {
    window.dispatchNotify({ type: 'info', message: `工作日${text}` })
    return false
  }
  if (!isTimeSlotValid(strategys.rest) || !coverTest(strategys.rest)) {
    window.dispatchNotify({ type: 'info', message: `休息日${text}` })
    return false
  }
  return true
}

// ==== 行拖拽排序（保持原有逻辑） ====
let mousedown = false
let currentRow = null
const barTop = ref(0)
const visible = ref(false)

function onSortBtnDown(row) {
  mousedown = true
  currentRow = row
}

function handlerMouseUp() {
  mousedown = false
  visible.value = false
  currentRow = null
}

function getLocationPosition(target, pageX, pageY) {
  const { left, top, height } = target.getBoundingClientRect()
  return { x: pageX - left, y: pageY - top, height }
}

function handlerMouseMove(event) {
  if (!mousedown) return
  visible.value = true
  if (tableData.value.length === 0) return

  const target = document.querySelector('.tpl-strategy-editor .el-table__body-wrapper')
  if (!target) return
  const scrollWrap = target.querySelector('.el-scrollbar__wrap')
  const row = document.querySelector('.tpl-strategy-editor .el-table__row')
  if (!scrollWrap || !row) return
  const { y, height: targetHeight } = getLocationPosition(target, event.pageX, event.pageY)
  const { height: rowHeight } = row.getBoundingClientRect()

  const targetIndex = Math.floor((y + scrollWrap.scrollTop + rowHeight / 2) / rowHeight)
  if (targetIndex <= -1 || targetIndex > tableData.value.length) return

  const hd = document.querySelector('.tpl-strategy-editor .el-table__header-wrapper')
  const { height } = hd.getBoundingClientRect()
  const srcIndex = tableData.value.indexOf(currentRow)

  if (y > targetHeight) tableRef.value.setScrollTop(rowHeight * targetIndex)
  else if (y < 0) tableRef.value.setScrollTop(0)

  if (srcIndex < targetIndex) {
    if (targetIndex - srcIndex > 1) {
      tableData.value.splice(srcIndex, 1)
      tableData.value.splice(targetIndex - 1, 0, currentRow)
    }
  } else if (srcIndex > targetIndex) {
    tableData.value.splice(srcIndex, 1)
    tableData.value.splice(targetIndex, 0, currentRow)
  }

  barTop.value = (targetIndex + 1) * rowHeight + height - scrollWrap.scrollTop + 10
}

function inputPowerChange(e) {
  if (e.target.value === '') {
    window.dispatchNotify({ type: 'info', message: '功率不能为空，请输入！' })
  }
}

function isInteger(value) {
  const num = parseFloat(value)
  return num > 0 ? num === Math.floor(value) : num === Math.round(value)
}

function onUpperLimitChange(row) {
  if (Number(row.upper_limit) < Number(row.lower_limit)) {
    row.upper_limit = row.lower_limit
    window.dispatchNotify({ type: 'info', message: '放电下限不能高于充电上限，已自动修正' })
  }
  if (row.upper_limit === '') {
    window.dispatchNotify({ type: 'info', message: '充电上限不能为空，请输入！' })
  }
}

function onLowerLimitChange(row) {
  if (Number(row.lower_limit) > Number(row.upper_limit)) {
    row.lower_limit = row.upper_limit
    window.dispatchNotify({ type: 'info', message: '充电上限不能低于放电下限，已自动修正' })
  }
  if (row.lower_limit === '') {
    window.dispatchNotify({ type: 'info', message: '放电下限不能为空，请输入！' })
  }
}

onMounted(() => {
  document.addEventListener('mousemove', handlerMouseMove)
  document.addEventListener('mouseup', handlerMouseUp)
})

onUnmounted(() => {
  document.removeEventListener('mousemove', handlerMouseMove)
  document.removeEventListener('mouseup', handlerMouseUp)
})

defineExpose({ updateLocalStrategy, getStrategys, checkValidate, startEdit, exitEdit })
</script>

<style scoped lang="scss">
.tpl-strategy-editor {
  $top-container-height: 50px;
  width: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 16px;

  .top-container {
    height: $top-container-height;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .operate-btns-container .el-button.is-plain {
      border: 1px solid currentColor;

      &.edit { color: #3A68FF; border-color: #3A68FFAA; &:hover { background: #3A68FF33; } }
      &.copy { color: #fff; background: #FF1942; border-color: #FF1942; &:hover { background: #FF3D5F; border-color: #FF3D5F; } }
      &.paste,
      &.clear { color: #444A6D; border-color: #444A6DAA; &:hover { background: #444A6D33; } }
    }
  }

  .main-container {
    position: relative;
    display: flex;
    flex-direction: column;

    ::v-deep .el-table {
      .el-table__header .el-table__cell {
        height: 50px;
        .cell { color: #96A5B8; display: flex; justify-content: center; font-size: 12px !important; }
      }

      .el-table__body .el-table__row:hover {
        background-color: #ffffff !important;
        td.el-table__cell { background-color: #39456606 !important; }
      }

      .table-cell {
        .cell { padding: 0 8px; }

        .cell-content {
          background: transparent;
          width: 100%;
          height: 50px;
          display: flex;
          justify-content: space-around;
          align-items: center;

          .s-underline-input {
            background: transparent;
            text-align: center;
            border-width: 0 0 2px 0;
            border-color: transparent;
            outline: none;
          }
        }

        .mode-cell {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 0 6px;

          .mode-select {
            width: 100%;
            :deep(.el-input__wrapper) { padding: 0 8px; }
            :deep(.el-select__placeholder),
            :deep(.el-input__inner) { font-size: 12px; }
          }
        }

        .operate-cell-container {
          display: flex;
          justify-content: space-around;
          align-items: center;

          .el-button.is-plain {
            border-width: 0;
            background: none;

            &.add { color: #36B60C; &:hover { filter: brightness(1.2); } }
            &.delete { color: #FF1E1E; &:hover { filter: brightness(1.2); } }
            &.move { color: #3A68FF; &:hover { filter: brightness(1.2); } &.cursor-move { cursor: move; } }
          }
        }
      }
    }

    .move-bar {
      z-index: 2;
      width: 100%;
      background: #3A68FF;
      position: absolute;
      top: 0;
      height: 2px;
      display: flex;
      align-items: center;

      &::before {
        content: '';
        width: 10px;
        height: 10px;
        border: 3px solid #06b6d4;
        border-radius: 50%;
        position: relative;
        left: -7px;
      }
    }

    .el-button.add-btn {
      width: 100%;
      height: 50px;
      color: #000;
    }

    .chart-container {
      height: 240px !important;
      margin-top: 8px;
    }
  }
}

::-webkit-calendar-picker-indicator {
  filter: invert(60%);
}
</style>
